/**
 * Gefäße in der Seitenansicht: Schüssel, Topf und Pfanne.
 *
 * Jedes Gefäß liefert drei Ebenen, damit Werkzeuge glaubwürdig „im“ Gefäß
 * stecken: die Rückseite mit der Innenfläche, den Inhalt und die Vorderwand.
 * Alles, was zwischen Rückseite und Vorderwand gezeichnet wird, verschwindet
 * unterhalb des vorderen Rands hinter der Wand.
 */
import type { Point } from '../geometry.ts';
import { STROKE } from '../palette.ts';
import type { Tone } from '../palette.ts';
import { ellipsePath, path, roundedRectPath } from '../paths.ts';
import { ellipse, group, rect, shape, styles } from '../scene.ts';
import type { MaybeNode, SceneNode } from '../scene.ts';

/** Ebenen eines Gefäßes. */
export interface Vessel {
  /** Innenfläche und hinterer Rand. */
  readonly back: SceneNode[];
  /** Vorderwand mit vorderem Rand. */
  readonly front: SceneNode[];
  /** Öffnung als Pfad, zum Beschneiden des Inhalts. */
  readonly opening: string;
  /** Umriss der Vorderwand (Körper unterhalb des Rands). */
  readonly body: string;
  readonly cx: number;
  readonly rimY: number;
  readonly rx: number;
  readonly ry: number;
  /** Tiefe vom Rand bis zum Boden innen. */
  readonly depth: number;
  /** Halbe Breite der Innenfläche in einer bestimmten Tiefe (0 Rand, 1 Boden). */
  readonly widthAt: (level: number) => number;
  /** Punkte auf dem äußeren Umriss, zum Beispiel zum Aufsetzen beim Kippen. */
  readonly contour: readonly Point[];
}

/** Punkt auf einer kubischen Bézierkurve. */
function bezier(p0: Point, p1: Point, p2: Point, p3: Point, t: number): Point {
  const u = 1 - t;
  return {
    x: u * u * u * p0.x + 3 * u * u * t * p1.x + 3 * u * t * t * p2.x + t * t * t * p3.x,
    y: u * u * u * p0.y + 3 * u * u * t * p1.y + 3 * u * t * t * p2.y + t * t * t * p3.y,
  };
}

/** Punkte auf dem oberen Bogen einer Randellipse. */
function rimPoints(cx: number, rimY: number, rx: number, ry: number): Point[] {
  return Array.from({ length: 9 }, (_, index) => {
    const angle = Math.PI + (index / 8) * Math.PI;
    return { x: cx + Math.cos(angle) * rx, y: rimY + Math.sin(angle) * ry };
  });
}

/**
 * Beschnitt für den Inhalt: obere Hälfte der Öffnung und alles darunter. Was
 * unterhalb des vorderen Rands liegt, verdeckt ohnehin die Vorderwand.
 */
export function interiorClip(vessel: Vessel, above = 0): string {
  const top = path()
    .move(vessel.cx - vessel.rx, vessel.rimY)
    .line(vessel.cx - vessel.rx, vessel.rimY - above)
    .line(vessel.cx + vessel.rx, vessel.rimY - above)
    .line(vessel.cx + vessel.rx, vessel.rimY)
    .close()
    .toString();
  return `${above > 0 ? top : ''}${vessel.opening}${vessel.body}`;
}

/**
 * Oberfläche eines Inhalts (Teig, Wasser, Schaum) als Ellipse in der Öffnung.
 * @param level 0 = randvoll, 1 = Boden.
 */
export function contents(
  vessel: Vessel,
  level: number,
  fill: Tone,
  extra: readonly MaybeNode[] = [],
): SceneNode {
  const y = vessel.rimY + vessel.depth * level;
  const rx = vessel.widthAt(level);
  const ry = vessel.ry * (rx / vessel.rx);
  return group(
    [ellipse(vessel.cx, y, rx, ry, { fill, stroke: 'ink', strokeWidth: STROKE.detail }), group(extra)],
    { clip: vessel.opening },
  );
}

/** Rührschüssel mit rundem Bauch und Standring. */
export function bowl(cx: number, rimY: number, width: number, height: number, fill: Tone = 'paper'): Vessel {
  const rx = width / 2;
  const ry = Math.max(5, width * 0.13);
  const bottom = rimY + height;
  const front = path()
    .move(cx - rx, rimY)
    .arc(rx, ry, false, false, cx + rx, rimY)
    .curve(cx + rx, rimY + height * 0.62, cx + rx * 0.58, bottom, cx + rx * 0.3, bottom)
    .line(cx - rx * 0.3, bottom)
    .curve(cx - rx * 0.58, bottom, cx - rx, rimY + height * 0.62, cx - rx, rimY)
    .close()
    .toString();
  const opening = ellipsePath(cx, rimY, rx, ry);
  return {
    back: [shape(opening, { fill: 'shade', stroke: 'ink', strokeWidth: STROKE.outline })],
    front: [
      rect(cx - rx * 0.32, bottom - 1, rx * 0.64, 5, 2, styles.solid(fill)),
      shape(front, styles.solid(fill)),
      // Glanzlinie auf dem Bauch
      shape(
        path()
          .move(cx - rx * 0.72, rimY + ry + 4)
          .quad(cx - rx * 0.66, rimY + height * 0.55, cx - rx * 0.36, rimY + height * 0.8)
          .toString(),
        { stroke: fill === 'paper' ? 'tint' : 'paper', strokeWidth: STROKE.outline },
      ),
    ],
    opening,
    body: front,
    cx,
    rimY,
    rx,
    ry,
    depth: height * 0.8,
    widthAt: (level) => rx * (1 - 0.42 * level * level),
    contour: [
      ...rimPoints(cx, rimY, rx, ry),
      ...[1, -1].flatMap((side) =>
        Array.from({ length: 9 }, (_, index) =>
          bezier(
            { x: cx + side * rx, y: rimY },
            { x: cx + side * rx, y: rimY + height * 0.62 },
            { x: cx + side * rx * 0.58, y: bottom },
            { x: cx + side * rx * 0.3, y: bottom },
            index / 8,
          ),
        ),
      ),
      { x: cx - rx * 0.32, y: bottom + 4 },
      { x: cx + rx * 0.32, y: bottom + 4 },
    ],
  };
}

/** Kochtopf mit geraden Wänden und zwei Griffen. */
export function pot(cx: number, rimY: number, width: number, height: number): Vessel {
  const rx = width / 2;
  const ry = Math.max(5, width * 0.14);
  const bottom = rimY + height;
  const front = path()
    .move(cx - rx, rimY)
    .arc(rx, ry, false, false, cx + rx, rimY)
    .line(cx + rx, bottom - 4)
    .quad(cx + rx, bottom, cx + rx - 4, bottom)
    .line(cx - rx + 4, bottom)
    .quad(cx - rx, bottom, cx - rx, bottom - 4)
    .close()
    .toString();
  const opening = ellipsePath(cx, rimY, rx, ry);
  const handle = (side: -1 | 1): SceneNode =>
    shape(roundedRectPath(side < 0 ? cx - rx - 14 : cx + rx - 2, rimY + 6, 16, 8, 4), styles.solid('ink'));
  return {
    back: [
      handle(-1),
      handle(1),
      shape(opening, { fill: 'deep', stroke: 'ink', strokeWidth: STROKE.outline }),
    ],
    front: [
      shape(front, styles.solid('shade')),
      shape(
        path()
          .move(cx - rx, rimY + 12)
          .line(cx + rx, rimY + 12)
          .toString(),
        { stroke: 'deep', strokeWidth: STROKE.fine },
      ),
      shape(
        path()
          .move(cx - rx * 0.7, rimY + ry + 6)
          .line(cx - rx * 0.7, bottom - 7)
          .toString(),
        {
          stroke: 'tint',
          strokeWidth: STROKE.outline,
        },
      ),
    ],
    opening,
    body: front,
    cx,
    rimY,
    rx,
    ry,
    depth: height * 0.85,
    widthAt: () => rx,
    contour: [...rimPoints(cx, rimY, rx, ry), { x: cx - rx, y: bottom }, { x: cx + rx, y: bottom }],
  };
}

/** Pfanne mit flachem Rand und langem Stiel nach rechts. */
export function pan(cx: number, rimY: number, width: number): Vessel {
  const rx = width / 2;
  const ry = width * 0.2;
  const height = 13;
  const bottom = rimY + height;
  const front = path()
    .move(cx - rx, rimY)
    .arc(rx, ry, false, false, cx + rx, rimY)
    .line(cx + rx * 0.9, bottom)
    .arc(rx * 0.9, ry * 0.5, false, true, cx - rx * 0.9, bottom)
    .close()
    .toString();
  const opening = ellipsePath(cx, rimY, rx, ry);
  const handle = path()
    .move(cx + rx - 3, rimY + 2)
    .line(cx + rx + 50, rimY - 7)
    .quad(cx + rx + 56, rimY - 8, cx + rx + 56, rimY - 3)
    .quad(cx + rx + 56, rimY + 1, cx + rx + 50, rimY + 1)
    .line(cx + rx - 1, rimY + 9)
    .close()
    .toString();
  return {
    back: [
      shape(handle, styles.solid('ink')),
      shape(opening, { fill: 'deep', stroke: 'ink', strokeWidth: STROKE.outline }),
      // Pfannenboden innen
      shape(ellipsePath(cx, rimY + 4, rx * 0.86, ry * 0.72), { fill: 'shade', stroke: 'none' }),
    ],
    front: [shape(front, styles.solid('ink'))],
    opening,
    body: front,
    cx,
    rimY,
    rx,
    ry,
    depth: 5,
    widthAt: () => rx * 0.86,
    contour: [
      ...rimPoints(cx, rimY, rx, ry),
      { x: cx - rx * 0.9, y: bottom + ry * 0.5 },
      { x: cx + rx * 0.9, y: bottom + ry * 0.5 },
      { x: cx, y: bottom + ry * 0.5 },
    ],
  };
}

/**
 * Backform (Springform) von der Seite: gerade Wände, Spannverschluss vorn.
 * @param rimY Höhe des Rands; der Boden liegt bei rimY + height.
 */
export function cakeTin(cx: number, rimY: number, width: number, height: number): Vessel {
  const rx = width / 2;
  const ry = Math.max(5, width * 0.16);
  const bottom = rimY + height;
  const front = path()
    .move(cx - rx, rimY)
    .arc(rx, ry, false, false, cx + rx, rimY)
    .line(cx + rx, bottom)
    .arc(rx, ry, false, true, cx - rx, bottom)
    .close()
    .toString();
  const opening = ellipsePath(cx, rimY, rx, ry);
  return {
    back: [shape(opening, { fill: 'shade', stroke: 'ink', strokeWidth: STROKE.outline })],
    front: [
      shape(front, styles.solid('tint')),
      // Spannverschluss
      rect(cx + rx * 0.3, rimY + ry + height * 0.25, 14, 7, 2, styles.solid('deep')),
      shape(
        path()
          .move(cx - rx * 0.75, rimY + ry * 0.7 + 4)
          .line(cx - rx * 0.75, bottom + ry * 0.6 - 4)
          .toString(),
        {
          stroke: 'paper',
          strokeWidth: STROKE.outline,
        },
      ),
    ],
    opening,
    body: front,
    cx,
    rimY,
    rx,
    ry,
    depth: height,
    widthAt: () => rx,
    contour: [
      ...rimPoints(cx, rimY, rx, ry),
      { x: cx - rx, y: bottom },
      { x: cx + rx, y: bottom },
      { x: cx, y: bottom + ry },
    ],
  };
}
